import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alerta, Caminho, SeletorDeEmpresa, Tabela } from "@plantus/ui";
import type { OrdemDaTabela } from "@plantus/ui";
import { producao } from "@plantus/api-client";
import type { Empresa, Lote, RecorteDeLotes } from "@plantus/api-client";

import { usarEmpresas } from "../../../nucleo/empresa/ProvedorDeEmpresas";
import { caminhoDaRota } from "../../../nucleo/navegacao/caminho";
import { ROTAS } from "../../../nucleo/navegacao/rotas";
import { FormularioDeRecebimento } from "./FormularioDeRecebimento";
import { ResumoDaQuarentena } from "./ResumoDaQuarentena";
import { BarraDosLotes, FaixaDeFiltros } from "./PaginaLotesBusca";
import { colunasDosLotes } from "./PaginaLotesColunas";
import { legendaDosLotes } from "./PaginaLotesLegenda";
import {
  FILTRO_VAZIO, ORDEM_PADRAO, alternar, contarFiltros, ordemEmPalavras,
  ordemNaConsulta, periodoInvertido, temFiltro,
} from "./ordemDosLotes";
import type { FiltroDeLotes } from "./ordemDosLotes";
import "./PaginaLotes.css";

/**
 * Os lotes de matéria-prima — de uma produtora ou das duas.
 *
 * <h2>🔴 A COMPOSIÇÃO É UMA GRADE, e a ordem dela é a DO TELEFONE</h2>
 *
 * <p>Esta tela era uma PILHA encostada à esquerda: em 1920 a barra de ações
 * media 768 (854 de papel à direita) e o resumo da quarentena 578 (1 044), e
 * empilhados gastavam 519px antes do primeiro lote. Ela opta pela grade do
 * painel (ADR-0013 §4) e cada bloco diz onde pousa: <b>1. em qual empresa
 * estou?</b> (cabeçalho — fileira); <b>2. onde está X?</b> (busca e filtros —
 * coluna); <b>3. o que faço agora?</b> (receber material, na ponta oposta da
 * mesma barra — `PaginaLotesBusca.tsx`); <b>4. como estamos?</b> (o resumo do
 * que aguarda decisão — coluna).
 *
 * <p>🔴 <b>A primeira redação pôs o resumo ANTES da barra</b>, porque num painel
 * de mesa "como estamos" se lê antes de "onde está X". Medido em Chrome com a
 * grade montada, a barra de busca <b>desceu</b>: em 360 de y=377,9 para
 * <b>974,8</b> (+596,9 — o cartão da quarentena tem 580,9) e em 768 de 281,9
 * para <b>828,8</b>. Em 360px isso é enterrar a busca abaixo de duas dobras, e
 * este sistema é consultado <b>em pé, na frente da prateleira</b> (issue #11):
 * no telefone ninguém abre esta tela para ler um resumo, abre para
 * <b>achar um lote</b>. A ordem do DOM passou a ser a do telefone, e a mesa não
 * a desfaz — a grade só põe os dois lado a lado, achar à esquerda e resumo à
 * direita. Uma ordem, um lugar: nada de `order` em CSS, que separaria o que se
 * lê do que se tabula.
 *
 * <h2>⚠️ OS AVISOS MUDARAM DE ENDEREÇO, E NÃO FOI ARRUMAÇÃO</h2>
 *
 * <p>Os três alertas eram filhos diretos de <code>.lotes</code>. Hoje moram
 * dentro da <code>&lt;section&gt;</code> da lista, porque os três são afirmações
 * <b>sobre a lista</b> — por que ela está vazia, por que ela é o último recorte
 * válido, por que ela não existe. Dentro dela continuam indo ao papel
 * (`.lotes__lista` está na lista de permitidos de `app.css`) e param de disputar
 * posição na grade.
 *
 * <h2>O consolidado funciona, e é o selo que o torna aceitável</h2>
 *
 * <p>Cada linha carrega o selo da empresa dona do material: sem ele, dois lotes
 * de CNPJs diferentes ficariam idênticos um embaixo do outro, e material
 * atribuído à empresa errada é documento inválido (ADR-0005 §10). O consolidado
 * <b>não recebe material</b> e <b>não soma quarentena</b> (somar dois CNPJs daria
 * um total que não é de ninguém), e a distribuidora continua fora dele.
 */
export function PaginaLotes() {
  const { lista, carregando, escolha, atual, consolidado, escolher } = usarEmpresas();
  const navegar = useNavigate();

  // Só produtoras: oferecer a distribuidora seria oferecer um caminho que o
  // servidor recusa — e a pessoa descobriria depois de digitar o recebimento.
  const produtoras = useMemo(() => lista.filter((e) => e.papel === "PRODUTORA"), [lista]);

  // Uma quando há escolha, TODAS as produtoras no consolidado — do mesmo
  // conjunto que alimenta o seletor, para a distribuidora não entrar por ali.
  const empresasConsultadas = useMemo<Empresa[]>(() => {
    if (consolidado) return produtoras;
    return atual !== null && atual.papel === "PRODUTORA" ? [atual] : [];
  }, [consolidado, produtoras, atual]);

  const [lotes, definirLotes] = useState<Lote[]>([]);
  const [filtro, definirFiltro] = useState<FiltroDeLotes>(FILTRO_VAZIO);
  const [ordem, definirOrdem] = useState<OrdemDaTabela | null>(ORDEM_PADRAO);
  // A faixa nasce FECHADA por medição: aberta custava 252px antes do 1º lote.
  const [filtrando, definirFiltrando] = useState(false);
  const [recebendo, definirRecebendo] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  // A chave é a lista de identificadores, não o arranjo: ele renasce a cada
  // render e a busca dispararia em laço.
  const chaveDasEmpresas = empresasConsultadas.map((e) => e.id).join(",");
  const invertido = periodoInvertido(filtro.de, filtro.ate);
  // 🔴 SÓ OS RECORTES, e a ordem NÃO é um deles: um clique em "Validade" sem
  // filtro dizia "recortada por 1 filtro(s)" sobre a lista inteira.
  const quantosFiltros = contarFiltros(filtro);

  // A tabela e a legenda usam as MESMAS colunas: o nome da coluna ordenada é o
  // mesmo texto do cabeçalho, e um segundo mapa criaria a divergência.
  const colunas = useMemo(() => colunasDosLotes(navegar, consolidado), [navegar, consolidado]);
  const ordenacao = ordemEmPalavras(ordem, (c) => colunas.find((x) => x.chave === c)?.rotulo);

  // O RECORTE INTEIRO — busca, situações, período e ordem — numa consulta só, e
  // resolvido no SERVIDOR (1.3.1 R15). Nenhuma razão é de desempenho: a
  // quantidade chega como TEXTO; o recorte por empresa é do servidor (ADR-0003
  // §5); duas páginas ordenadas não se juntam; e `vencido` é calculado lá (R8).
  useEffect(() => {
    const ids = chaveDasEmpresas === "" ? [] : chaveDasEmpresas.split(",");
    if (ids.length === 0) {
      definirLotes([]);
      return;
    }
    // Período invertido NÃO vai ao servidor: a recusa dele chegaria aqui como
    // falha da consulta, e a tela diria "não foi possível carregar os lotes".
    if (periodoInvertido(filtro.de, filtro.ate)) return;

    const recorte: RecorteDeLotes = {
      ...(filtro.situacoes.length > 0 ? { situacao: filtro.situacoes } : {}),
      ...(filtro.de !== "" ? { recebidoDe: filtro.de } : {}),
      ...(filtro.ate !== "" ? { recebidoAte: filtro.ate } : {}),
      ...ordemNaConsulta(ordem),
    };

    definirErro(null);
    producao
      .lotes(ids, filtro.termo, recorte)
      // Falha aqui é a lista INTEIRA: meia lista consolidada apareceria
      // completa, sem o material de um CNPJ.
      .then(definirLotes)
      .catch(() => definirErro("Não foi possível carregar os lotes."));
  }, [chaveDasEmpresas, filtro, ordem]);

  // Sair do consolidado com o formulário aberto deixaria um recebimento pela
  // metade sem empresa.
  useEffect(() => {
    if (atual === null) definirRecebendo(false);
  }, [atual]);

  if (carregando) return <p role="status">Carregando empresas…</p>;

  const semEscolha = !consolidado && (atual === null || atual.papel !== "PRODUTORA");
  const buscando = !semEscolha && !recebendo;

  return (
    /*
     * ⚠️ Os dois atributos respondem perguntas DIFERENTES: largura é "qual é o
     * teto deste conteúdo?" (a LEI DE LARGURA, em `app.css`) e grade é
     * "quantas colunas ele ocupa?" (ADR-0013 §4). 🔴 E A LARGURA NÃO TROCA
     * QUANDO O RECEBIMENTO ABRE: o formulário entra ACIMA da lista, e trocar a
     * raiz colapsava a coluna de 1 664px para 768px, fazendo a tabela (802,9px)
     * TRANSBORDAR de uma caixa útil de 736px. O teto de entrada é do BLOCO.
     */
    <div className="lotes" data-largura="densa" data-grade="painel">
      {/* 1 — EM QUAL EMPRESA ESTOU? O caminho vem do MESMO mapa que desenha a
          lateral, então renomear um módulo renomeia o caminho e não deixa uma
          string velha para trás — o que já aconteceu duas vezes. */}
      <header className="lotes__topo" data-bloco="fileira">
        <div>
          <Caminho passos={caminhoDaRota(ROTAS.lotes)} aoNavegar={navegar} />
          <h1 className="lotes__titulo">Matérias-primas recebidas</h1>
          <p className="lotes__subtitulo">
            {consolidado
              ? `${produtoras.map((e) => e.apelido).join(" · ")} — cada linha diz de qual`
              : (atual?.nomeFantasia ?? "Escolha a empresa")}
          </p>
        </div>
        <SeletorDeEmpresa
          empresas={produtoras.map((e) => ({
            id: e.id,
            nome: e.razaoSocial,
            apelido: e.apelido,
            papel: e.papel,
          }))}
          selecionada={escolha ?? ""}
          aoSelecionar={escolher}
        />
      </header>

      {/* 2 e 3 — ONDE ESTÁ X? e O QUE FAÇO AGORA? Primeiro no DOM porque é
          primeiro no telefone. */}
      {buscando && (
        <BarraDosLotes
          data-bloco="coluna"
          filtro={filtro}
          definirFiltro={definirFiltro}
          filtrando={filtrando}
          definirFiltrando={definirFiltrando}
          podeReceber={atual !== null}
          aoReceber={() => definirRecebendo(true)}
        />
      )}

      {/* ⚠️ A FAIXA VEM COLADA NO GATILHO QUE A ABRE. Depois do resumo, no
          telefone, ela nasceria 580px abaixo do botão "Filtros" — fora da tela,
          e o toque pareceria não ter feito nada. O preço é que, ABERTA, ela
          empurra o resumo para a fileira seguinte na mesa: estado transitório,
          e a faixa nasce FECHADA por medição. */}
      {buscando && filtrando && (
        <FaixaDeFiltros
          data-bloco="fileira"
          filtro={filtro}
          definirFiltro={definirFiltro}
          ordem={ordem}
          definirOrdem={definirOrdem}
          invertido={invertido}
        />
      )}

      {/* 🔴 FILEIRA, e não coluna: enquanto o formulário está aberto ele É o
          assunto da tela. Como coluna ele caía no traço da FICHA e recebia
          402px em 1280 — um formulário cujo `<select>` mais longo pede 367 e
          cujo teto medido é 768 (`--largura-entrada`, na folha da tela). */}
      {recebendo && atual !== null && (
        <div className="lotes__recebimento" data-bloco="fileira">
          <FormularioDeRecebimento
            empresaId={atual.id}
            aoCancelar={() => definirRecebendo(false)}
            aoReceber={(id) => {
              definirRecebendo(false);
              navegar(`/producao/materias-primas/${id}/etiqueta?empresa=${atual.id}`);
            }}
          />
        </div>
      )}

      {/* 4 — COMO ESTAMOS? O invólucro existe para a IMPRESSÃO: sem esta
          classe a lista de permitidos não separaria "o resumo, DADO" do
          "formulário, ENTRADA" — os dois devolvem `Cartao`. */}
      {!semEscolha && !consolidado && atual !== null && (
        <div className="lotes__quarentena" data-bloco="coluna">
          <ResumoDaQuarentena empresaId={atual.id} />
        </div>
      )}
      {!semEscolha && consolidado && (
        <p className="lotes__vazio" data-bloco="coluna">
          <strong>Receber material</strong> exige uma empresa escolhida — não
          existe lote de todas. O <strong>resumo da quarentena</strong> também:
          ele soma quantidade por matéria-prima, e somar dois CNPJs daria um
          total que não é de ninguém.
        </p>
      )}

      {/* A LISTA, SEM CARTÃO EM VOLTA: quem a nomeia para quem ouve a tela é o
          `aria-label` e a legenda. ⚠️ O CARTÃO DE CADA LINHA NO CELULAR NÃO
          SAIU: abaixo de 52rem cada linha vira cartão, e isso é do `Tabela`. */}
      <section className="lotes__lista" aria-label="Lotes recebidos" data-bloco="fileira">
        {semEscolha ? (
          <Alerta tom="atencao" titulo="Escolha a empresa">
            Material pertence a um CNPJ, e o sistema não escolhe por você — ou
            escolha <strong>Todas as empresas</strong>, onde cada linha diz a
            quem o material pertence.
            {produtoras.length < lista.length && (
              <>
                {" "}
                Só aparecem as <strong>produtoras</strong>: distribuidora compra
                produto acabado e revende, e não trabalha com matéria-prima.
              </>
            )}
          </Alerta>
        ) : (
          <>
            {erro !== null && <Alerta tom="erro">{erro}</Alerta>}

            {/* A recusa fica NA TELA e a consulta não sai: lista vazia aqui se
                leria como "não há material neste intervalo". */}
            {invertido && (
              <Alerta tom="atencao" titulo="Período invertido">
                O início do período vem <strong>depois</strong> do fim. Enquanto
                as duas datas estiverem trocadas, a lista continua mostrando o
                último recorte válido — ela não vai vazia, que se leria como
                &quot;não há material neste intervalo&quot;.
              </Alerta>
            )}

            {lotes.length === 0 ? (
              <p className="lotes__vazio">
                {/* 🔴 `temFiltro`, e não "filtro OU ordem": ordenar não esconde
                    lote nenhum, e um clique em "Validade" num estoque vazio
                    mandava procurar um recorte que não existia. */}
                {temFiltro(filtro)
                  ? "Nenhum lote encontrado para esta busca."
                  : "Nenhum lote recebido ainda. Ao receber, o sistema gera o código interno e a etiqueta de quarentena."}
              </p>
            ) : (
              <Tabela
                colunas={colunas}
                linhas={lotes}
                chave={(lote) => lote.id}
                legenda={legendaDosLotes(lotes, consolidado, quantosFiltros, ordenacao)}
                ordem={ordem}
                // Quem reordena é o SERVIDOR: ordenar as linhas já carregadas
                // poria `"120.000000"` antes de `"25.000000"`.
                aoOrdenar={(chave) => definirOrdem(alternar(ordem, chave))}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}
